import { Controller } from '@hotwired/stimulus';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { fillField, debounce } from '../lib/form_utils';

// Singleton: first caller's API key wins
let loaderPromise = null;

function getPlacesLibrary(apiKey) {
  if (!loaderPromise) {
    setOptions({key: apiKey});
    loaderPromise = importLibrary('places').catch((e) => {
      loaderPromise = null;
      throw e;
    });
  }
  return loaderPromise;
}

export default class extends Controller {
  static targets = [
    'addressInput',
    'clearBtn',
    'suggestions',
    'suggestionTemplate',
    'streetAddress',
    'postalCode',
    'locality',
    'country',
    'zipCodeError',
  ];

  static values = {
    apiKey: String,
    zipCodeValidationMessage: String,
  };

  async connect() {
    this._suggestions = [];
    this._activeIndex = -1;

    if (!this.apiKeyValue) {
      console.warn('[address-autocomplete] missing API key');
      return;
    }

    try {
      const places = await getPlacesLibrary(this.apiKeyValue);
      this._AutocompleteSuggestion = places.AutocompleteSuggestion;
      this._debouncedSearch = debounce(
        this._search.bind(this), 300
      );
    } catch (e) {
      console.error('[address-autocomplete] init failed', e);
    }
  }

  disconnect() {
    if (this._debouncedSearch) {
      this._debouncedSearch.cancel();
    }
  }

  onInput() {
    if (!this._debouncedSearch) return;

    const query = this.addressInputTarget.value.trim();

    if (query.length > 0) {
      this.clearBtnTarget.classList.remove('d-none');
    } else {
      this.clearBtnTarget.classList.add('d-none');
      this._closeSuggestions();
      return;
    }

    if (query.length < 3) return;

    this._debouncedSearch(query);
  }

  onKeydown(event) {
    if (!this.suggestionsTarget.classList.contains('show')) return;

    const items = this.suggestionsTarget.querySelectorAll('button');
    if (items.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this._activeIndex = Math.min(
          this._activeIndex + 1, items.length - 1
        );
        this._highlightItem(items);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this._activeIndex = Math.max(this._activeIndex - 1, 0);
        this._highlightItem(items);
        break;
      case 'Enter':
        event.preventDefault();
        if (this._activeIndex >= 0 && items[this._activeIndex]) {
          items[this._activeIndex].click();
        }
        break;
      case 'Escape':
        this._closeSuggestions();
        break;
      default:
        break;
    }
  }

  clear() {
    this.addressInputTarget.value = '';
    this.clearBtnTarget.classList.add('d-none');
    this._closeSuggestions();
    this.addressInputTarget.focus();
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this._closeSuggestions();
    }
  }

  async selectSuggestion(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (isNaN(index) || index < 0 || !this._suggestions[index]) return;

    const prediction = this._suggestions[index].placePrediction;

    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ['addressComponents'],
      });

      if (!this.element.isConnected) return;

      this._fillFields(place);
      this.addressInputTarget.value = prediction.text.text;
      this.dispatch('place-selected', {detail: {place}});
    } catch (e) {
      console.error('[address-autocomplete]', e);
    }

    this._closeSuggestions();
  }

  clearZipCodeError() {
    if (!this.zipCodeValidationMessageValue) return
    if (this.#isValidZipCode()) this.#hideZipCodeError()
  }

  validateZipCode() {
    if (!this.zipCodeValidationMessageValue) return

    if (!this.postalCodeTarget.value.trim()) {
      this.#hideZipCodeError()
      return
    }

    if (this.#isValidZipCode()) {
      this.#hideZipCodeError()
    } else {
      this.#showZipCodeError()
    }
  }

  // -- Private -------------------------------------------------------

  async _search(query) {
    try {
      const response = await this._AutocompleteSuggestion
        .fetchAutocompleteSuggestions({
          input: query,
          includedPrimaryTypes: ['geocode'],
        });

      if (!this.element.isConnected) return;

      this._renderSuggestions(response.suggestions);
    } catch (e) {
      console.error('[address-autocomplete]', e);
    }
  }

  _renderSuggestions(suggestions) {
    const template = this.suggestionTemplateTarget;
    this.suggestionsTarget.replaceChildren();

    suggestions.forEach((suggestion, i) => {
      const prediction = suggestion.placePrediction;
      const clone = template.content.cloneNode(true);
      const button = clone.querySelector('button');
      button.textContent = prediction.text.text;
      button.dataset.index = i;
      this.suggestionsTarget.appendChild(clone);
    });

    this.suggestionsTarget.classList.add('show');
    this._suggestions = suggestions;
    this._activeIndex = -1;
  }

  _highlightItem(items) {
    items.forEach((item) => item.classList.remove('active'));
    if (this._activeIndex >= 0 && items[this._activeIndex]) {
      items[this._activeIndex].classList.add('active');
    }
  }

  _fillFields(place) {
    if (!place.addressComponents) return;

    const componentMapping = {
      postal_code: this.postalCodeTarget,
      locality: this.localityTarget,
      country: this.countryTarget,
    };

    let streetNumber = '';
    let route = '';

    place.addressComponents.forEach((component) => {
      component.types.forEach((type) => {
        if (type === 'street_number') {
          streetNumber = component.shortText;
        } else if (type === 'route') {
          route = component.shortText;
        }

        const target = componentMapping[type];
        if (target) {
          fillField(target, component.shortText);
        }
      });
    });

    const streetAddress = [streetNumber, route]
      .filter(Boolean).join(' ');
    fillField(this.streetAddressTarget, streetAddress);
  }

  _closeSuggestions() {
    this.suggestionsTarget.classList.remove('show');
    this.suggestionsTarget.replaceChildren();
    this._activeIndex = -1;
  }

  #isValidZipCode() {
    if (!this.postalCodeTarget.value.trim()) return true
    if (this.countryTarget.value !== 'FR') return true

    return /^\d{5}$/.test(this.postalCodeTarget.value.trim())
  }

  #showZipCodeError() {
    this.postalCodeTarget.classList.add('is-invalid')
    this.zipCodeErrorTarget.textContent = this.zipCodeValidationMessageValue
    this.zipCodeErrorTarget.classList.remove('d-none')
  }

  #hideZipCodeError() {
    this.postalCodeTarget.classList.remove('is-invalid')
    this.zipCodeErrorTarget.classList.add('d-none')
  }
}
